/**
 * TanStack Query hooks for the SubContractorHub Document Filters tab (SCH-2.4).
 *
 * Mirrors useAdderMappings (SCH-2.3) — single discovery query as source of
 * truth (the discover-document-labels payload bundles existingFilters), so
 * the bulk-save mutation splices the response back into the same cache.
 *
 * Architectural note: Document Filters has only ONE mutation endpoint
 * (bulk-save). No per-row create / update / delete. The cache splice on
 * success replaces existingFilters with the bulk-save response.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { documentFilterService } from '@/services/integrations/documentFilterService';
import type { DiscoverDocumentLabelsResponse } from '@/types/integrations/documentFilter';

const FIVE_MINUTES = 5 * 60 * 1000;

function discoverDocumentLabelsQueryKey(tenantId: string | undefined) {
  return ['subcontractorhub', tenantId, 'discover-document-labels'] as const;
}

export function useDiscoverDocumentLabels() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useQuery({
    queryKey: discoverDocumentLabelsQueryKey(tenantId),
    queryFn: async () => {
      const result = await documentFilterService.discoverDocumentLabels();
      if (result.error) {
        const err = new Error(result.error.message) as Error & {
          code?: string;
          details?: Record<string, unknown>;
        };
        err.code = result.error.code;
        err.details = result.error.details;
        throw err;
      }
      return result.data;
    },
    enabled: !!tenantId,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
  });
}

export function useBulkSaveDocumentFilters() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverDocumentLabelsQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: documentFilterService.bulkSaveDocumentFilters,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      // Replace existingFilters with the bulk-save response. discoveredLabels
      // and joulesCategories are unchanged (the discovery payload is
      // independent of which filters are persisted — re-discover via
      // invalidate if you need fresh labels or category list).
      queryClient.setQueryData<DiscoverDocumentLabelsResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        return { ...prev, existingFilters: result.data.rows };
      });
    },
  });
}

/**
 * SCH-2.5: atomic create-category + map-filter mutation. Splices BOTH the
 * new category into joulesCategories AND the new filter row into
 * existingFilters (replace-or-append by external_label). Mirrors SCH-2.3's
 * useCreateAdderWithMapping cache strategy.
 */
export function useCreateFilterWithNewCategory() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverDocumentLabelsQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: documentFilterService.createDocumentFilterWithNewCategory,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const { category, filter } = result.data;
      queryClient.setQueryData<DiscoverDocumentLabelsResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        // Append the new category to joulesCategories (dedup by id — a parallel
        // discover may have raced and pulled it in).
        const categories = prev.joulesCategories.some((c) => c.id === category.id)
          ? prev.joulesCategories
          : [...prev.joulesCategories, category].sort((a, b) => {
              if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
              return a.displayLabel.localeCompare(b.displayLabel);
            });
        // Replace-or-append the filter row by external_label (UPSERT semantics).
        const filtered = prev.existingFilters.filter(
          (r) => r.externalLabel !== filter.externalLabel,
        );
        return {
          ...prev,
          joulesCategories: categories,
          existingFilters: [...filtered, filter],
        };
      });
    },
  });
}

export { discoverDocumentLabelsQueryKey };
